import { useEffect, useState } from "react";
import axios from "axios";
import apiurl from "../Api";

function Profile() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "",
    post: "",
  });
  const [staffGroups, setStaffGroups] = useState({});
  const [activeStaffTab, setActiveStaffTab] = useState("");
  const [loading, setLoading] = useState(true);
  const [staffRows, setStaffRows] = useState([]);
  const [expandedManagers, setExpandedManagers] = useState({});
  const [expandedHeads, setExpandedHeads] = useState({});
  const [expandedCxos, setExpandedCxos] = useState({});

  useEffect(() => {
    document.title = "Profile | MERAQUI";

    const fetchProfile = async () => {
      try {
        const response = await axios.get(apiurl + "profile.php?subdomain=react");
        const data = response.data?.data || response.data || {};
        const staff = data.staff && typeof data.staff === "object" ? data.staff : {};
        const rows = Array.isArray(data.staff_rows) ? data.staff_rows : [];
        const staffKeys = Object.keys(staff).filter(
          (key) => Array.isArray(staff[key]) && staff[key].length > 0
        );
        setForm({
          name: data.banda_naam || data.name || "",
          email: data.email || "",
          mobile: data.number || data.mobile || data.phone || "",
          post: data.post || data.loginpost || data.userpost || "",
        });
        setStaffGroups(staff);
        setStaffRows(rows);
        setActiveStaffTab((prev) => (prev && staffKeys.includes(prev) ? prev : staffKeys[0] || ""));
      } catch (error) {
        setForm({ name: "", email: "", mobile: "", post: "" });
        setStaffGroups({});
        setActiveStaffTab("");
        setStaffRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleChange = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const toggleManager = (id) => {
    setExpandedManagers((prev) => ({ ...prev, [id]: !prev[id] }));
  };
  const toggleHead = (id) => {
    setExpandedHeads((prev) => ({ ...prev, [id]: !prev[id] }));
  };
  const toggleCxo = (id) => {
    setExpandedCxos((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const staffTabs = Object.keys(staffGroups).filter(
    (key) => Array.isArray(staffGroups[key]) && staffGroups[key].length > 0
  );
  const uniqueStaffByTab = staffTabs.reduce((acc, key) => {
    const seen = new Set();
    const list = (staffGroups[key] || []).filter((person) => {
      const rawName = String(person?.name || "").trim();
      const normalized = rawName.toLowerCase();
      if (!normalized) return false;
      if (seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    });
    acc[key] = list;
    return acc;
  }, {});

  const hierarchy = (() => {
    const rows = Array.isArray(staffRows) ? staffRows : [];
    if (rows.length === 0) {
      return { mode: "flat" };
    }

    const cxoMap = new Map();
    const headMap = new Map();
    const managerMap = new Map();
    const cxoHeadIds = new Map();
    const headPrimaryCxo = new Map();
    const headManagerIds = new Map();
    const managerRecruiters = new Map();
    const linkedRecruiterIds = new Set();
    const unassignedRecruiters = [];
    const staffHeadList = Array.isArray(staffGroups?.sourcing_head) ? staffGroups.sourcing_head : [];
    const staffManagerList = Array.isArray(staffGroups?.sourcing_manager)
      ? staffGroups.sourcing_manager
      : [];
    const staffRecruiterList = Array.isArray(staffGroups?.recruiter) ? staffGroups.recruiter : [];

    const normalize = (value) => String(value || "").trim();
    const upsert = (map, id, name) => {
      const normId = normalize(id);
      const normName = normalize(name);
      const key = normId || normName;
      if (!key) return null;
      if (!map.has(key)) {
        map.set(key, { id: key, name: normName || key });
      } else if (normName && !map.get(key).name) {
        map.get(key).name = normName;
      }
      return map.get(key);
    };
    const link = (map, parentId, childId) => {
      if (!parentId || !childId) return;
      if (!map.has(parentId)) {
        map.set(parentId, new Set());
      }
      map.get(parentId).add(childId);
    };
    const addRecruiter = (managerId, recruiter) => {
      if (!managerId || !recruiter?.name) return;
      if (!managerRecruiters.has(managerId)) {
        managerRecruiters.set(managerId, []);
      }
      const list = managerRecruiters.get(managerId);
      const exists = list.some(
        (person) => person?.name?.toLowerCase() === recruiter.name.toLowerCase()
      );
      if (!exists) list.push(recruiter);
      if (recruiter?.id) linkedRecruiterIds.add(recruiter.id);
    };

    rows.forEach((row) => {
      const role = normalize(row?.gpost);

      const cxoFromRow = {
        id: normalize(row?.sid),
        name: normalize(row?.sname),
      };
      const headFromRow = {
        id: normalize(row?.shid),
        name: normalize(row?.shname),
      };
      const managerFromRow = {
        id: normalize(row?.shid),
        name: normalize(row?.shname),
      };
      const recruiterFromRow = {
        id: normalize(row?.shid),
        name: normalize(row?.shname),
      };
      const headParent = {
        id: normalize(row?.sid),
        name: normalize(row?.sname),
      };
      const headFromRecruiter = {
        id: normalize(row?.shid2),
        name: normalize(row?.shname2),
      };
      const cxoFromJoin = {
        id: normalize(row?.cxoid),
        name: normalize(row?.cxoname),
      };

      if (role === "sourcing_head") {
        const head = upsert(headMap, headFromRow.id, headFromRow.name);
        const cxoNode = upsert(cxoMap, cxoFromRow.id, cxoFromRow.name);
        if (head && cxoNode) {
          link(cxoHeadIds, cxoNode.id, head.id);
          headPrimaryCxo.set(head.id, cxoNode.id);
        }
        return;
      }

      if (role === "sourcing_manager") {
        const manager = upsert(managerMap, managerFromRow.id, managerFromRow.name);
        const head = upsert(headMap, headParent.id, headParent.name);
        if (manager && head) {
          link(headManagerIds, head.id, manager.id);
        }
        const cxoNode = upsert(cxoMap, cxoFromJoin.id, cxoFromJoin.name);
        if (cxoNode && head) {
          const existing = headPrimaryCxo.get(head.id);
          if (!existing || existing === cxoNode.id) {
            link(cxoHeadIds, cxoNode.id, head.id);
            headPrimaryCxo.set(head.id, cxoNode.id);
          }
        }
        return;
      }

      if (role === "recruiter") {
        const manager = upsert(managerMap, headParent.id, headParent.name);
        const head = upsert(headMap, headFromRecruiter.id, headFromRecruiter.name);
        if (manager && head) {
          link(headManagerIds, head.id, manager.id);
        }
        if (manager) {
          addRecruiter(manager.id, {
            id: recruiterFromRow.id || recruiterFromRow.name,
            name: recruiterFromRow.name,
          });
        } else if (recruiterFromRow.name) {
          unassignedRecruiters.push({
            id: recruiterFromRow.id || recruiterFromRow.name,
            name: recruiterFromRow.name,
          });
        }
        const cxoNode = upsert(cxoMap, cxoFromJoin.id, cxoFromJoin.name);
        if (cxoNode && head) {
          const existing = headPrimaryCxo.get(head.id);
          if (!existing || existing === cxoNode.id) {
            link(cxoHeadIds, cxoNode.id, head.id);
            headPrimaryCxo.set(head.id, cxoNode.id);
          }
        }
      }
    });

    staffHeadList.forEach((head) => {
      upsert(headMap, head?.id, head?.name);
    });
    staffManagerList.forEach((manager) => {
      upsert(managerMap, manager?.id, manager?.name);
    });

    const managers = Array.from(managerMap.values()).map((manager) => ({
      ...manager,
      recruiters: managerRecruiters.get(manager.id) || [],
    }));
    const managersById = new Map(managers.map((manager) => [manager.id, manager]));

    const heads = Array.from(headMap.values()).map((head) => {
      const managerIds = headManagerIds.get(head.id) || new Set();
      const linkedManagers = Array.from(managerIds)
        .map((id) => managersById.get(id))
        .filter(Boolean);
      return { ...head, managers: linkedManagers };
    });

    const cxos = Array.from(cxoMap.values()).map((cxo) => {
      const headIds = cxoHeadIds.get(cxo.id) || new Set();
      const linkedHeads = Array.from(headIds)
        .map((id) => headMap.get(id))
        .filter(Boolean)
        .map((head) => {
          const managerIds = headManagerIds.get(head.id) || new Set();
          const linkedManagers = Array.from(managerIds)
            .map((id) => managersById.get(id))
            .filter(Boolean);
          return { ...head, managers: linkedManagers };
        });
      return { ...cxo, heads: linkedHeads };
    });

    const linkedHeadIds = new Set();
    cxoHeadIds.forEach((set) => set.forEach((id) => linkedHeadIds.add(id)));

    const linkedManagerIds = new Set();
    headManagerIds.forEach((set) => set.forEach((id) => linkedManagerIds.add(id)));

    const unassignedHeads = heads.filter((head) => !linkedHeadIds.has(head.id));
    const unassignedManagers = managers.filter((manager) => !linkedManagerIds.has(manager.id));

    const unassignedRecruitersFromStaff = staffRecruiterList.filter((rec) => {
      const id = String(rec?.id || "").trim();
      return id && !linkedRecruiterIds.has(id);
    });

    const hasTree = cxos.length > 0 || heads.length > 0 || managers.length > 0;
    return {
      mode: hasTree ? "tree" : "flat",
      cxos,
      heads,
      managers,
      unassignedHeads,
      unassignedManagers,
      unassignedRecruiters: unassignedRecruitersFromStaff.length
        ? unassignedRecruitersFromStaff
        : unassignedRecruiters,
    };
  })();

  const isAdminView = ["ADMIN", "FOUNDER"].includes(String(form.post || "").toUpperCase());

  return (
    <div className="main-content app-content">
      <style>{`
        .profile-page .profile-hero {
          background:
            radial-gradient(120% 140% at 100% 0%, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 55%),
            linear-gradient(135deg, rgb(16, 40, 92) 0%, rgb(34, 74, 156) 48%, rgb(56, 118, 210) 100%);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 18px;
          color: #fff;
          overflow: hidden;
          position: relative;
          box-shadow: 0 18px 40px rgba(12, 46, 110, 0.25);
        }
        .profile-page .hero-art {
          position: absolute;
          right: -10px;
          top: -10px;
          opacity: 0.25;
        }
        .profile-page .profile-card {
          border-radius: 16px;
          border: 1px solid #e6edf5;
          box-shadow: 0 18px 36px rgba(15, 42, 80, 0.16);
          min-height: 520px;
        }
        .profile-page .avatar-badge {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.2);
          border: 2px solid rgba(255, 255, 255, 0.45);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          letter-spacing: 0.04em;
        }
        .profile-page .info-pill {
          background: #f1f5fb;
          color: #2b3a55;
          border-radius: 999px;
          padding: 4px 12px;
          font-size: 12px;
          font-weight: 600;
        }
        .profile-page .form-label {
          color: #5a6a85;
          font-weight: 600;
          font-size: 13px;
        }
        .profile-page .form-control {
          border-radius: 12px;
          border: 1px solid #d7e2f2;
          background: #fbfdff;
        }
        .profile-page .form-control:focus {
          border-color: #6aa7ff;
          box-shadow: 0 0 0 0.2rem rgba(88, 140, 255, 0.15);
        }
        .profile-page .floating-tag {
          position: absolute;
          right: 20px;
          bottom: 18px;
          background: rgba(255, 255, 255, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.3);
          color: #fff;
          font-size: 12px;
          padding: 6px 10px;
          border-radius: 999px;
          display: inline-flex;
          gap: 6px;
          align-items: center;
        }
        .profile-page .hero-details {
          margin-top: 14px;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 10px;
        }
        .profile-page .hero-detail {
          background: rgba(255, 255, 255, 0.18);
          border: 1px solid rgba(255, 255, 255, 0.25);
          border-radius: 12px;
          padding: 10px 12px;
          color: #fff;
          backdrop-filter: blur(4px);
        }
        .profile-page .hero-detail-label {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: rgba(255, 255, 255, 0.7);
        }
        .profile-page .hero-detail-value {
          font-weight: 600;
          margin-top: 2px;
        }
        .profile-page .nav-pills .nav-link:not(.active) {
          background: #e9edf3;
          color: #4b5563;
        }
        .profile-page .tree-wrap {
          display: flex;
          flex-direction: column;
          gap: 18px;
          align-items: stretch;
          overflow-x: auto;
          padding-bottom: 8px;
        }
        .profile-page .tree-level {
          display: flex;
          flex-wrap: nowrap;
          gap: 18px;
          justify-content: center;
          min-width: max-content;
        }
        .profile-page .tree-level.cxo-row {
          justify-content: flex-start;
          padding: 0 6px;
        }
        .profile-page .tree-node {
          position: relative;
          padding-top: 10px;
          flex: 0 0 auto;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .profile-page .tree-node:after {
          content: "";
          position: absolute;
          left: 50%;
          top: -6px;
          width: 2px;
          height: 6px;
          background: rgba(51, 65, 85, 0.55);
          transform: translateX(-50%);
        }
        .profile-page .tree-level.cxo-row .tree-node:after {
          display: none;
        }
        .profile-page .node-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          padding: 12px 14px;
          width: clamp(220px, 24vw, 260px);
          max-width: 260px;
          box-shadow: 0 12px 28px rgba(15, 42, 80, 0.12);
          transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease;
          position: relative;
          overflow: hidden;
        }
        .profile-page .node-card:before {
          content: "";
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 4px;
          background: #1e3a8a;
          opacity: 0.8;
        }
        .profile-page .node-card.clickable {
          cursor: pointer;
        }
        .profile-page .node-card.clickable:hover {
          transform: translateY(-2px);
          box-shadow: 0 18px 36px rgba(15, 42, 80, 0.16);
          border-color: rgba(59, 130, 246, 0.45);
        }
        .profile-page .node-card.role-cxo {
          background: linear-gradient(145deg, #fff7ed 0%, #ffedd5 100%);
          border-color: #fdba74;
        }
        .profile-page .node-card.role-cxo:before {
          background: #b45309;
        }
        .profile-page .node-card.role-head {
          background: linear-gradient(145deg, #eff6ff 0%, #dbeafe 100%);
          border-color: #93c5fd;
        }
        .profile-page .node-card.role-head:before {
          background: #1d4ed8;
        }
        .profile-page .node-card.role-manager {
          background: linear-gradient(145deg, #f0fdf4 0%, #dcfce7 100%);
          border-color: #86efac;
        }
        .profile-page .node-card.role-manager:before {
          background: #15803d;
        }
        .profile-page .node-card.role-recruiter {
          background: linear-gradient(145deg, #fdf2f8 0%, #fce7f3 100%);
          border-color: #f9a8d4;
        }
        .profile-page .node-card.role-recruiter:before {
          background: #9d174d;
        }
        .profile-page .node-main {
          display: flex;
          gap: 10px;
          align-items: center;
        }
        .profile-page .node-avatar {
          width: 38px;
          height: 38px;
          border-radius: 50%;
          object-fit: cover;
          border: 2px solid rgba(15, 42, 80, 0.18);
          box-shadow: 0 6px 14px rgba(15, 42, 80, 0.18);
        }
        .profile-page .node-role {
          font-size: 11px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #6b7280;
        }
        .profile-page .node-name {
          font-weight: 700;
          color: #1f2a44;
          margin-top: 4px;
        }
        .profile-page .node-sub {
          font-size: 12px;
          color: #64748b;
          margin-top: 4px;
        }
        .profile-page .tree-children {
          margin-top: 14px;
          display: flex;
          flex-wrap: wrap;
          gap: 14px;
          justify-content: center;
          position: relative;
          animation: treeFade 220ms ease;
          width: max-content;
          max-width: 100%;
        }
        .profile-page .tree-children:before {
          content: "";
          position: absolute;
          top: -10px;
          left: 50%;
          width: 2px;
          height: 12px;
          background: rgba(51, 65, 85, 0.55);
        }
        .profile-page .manager-actions {
          margin-top: 10px;
          display: flex;
          gap: 8px;
          align-items: center;
        }
        .profile-page .node-actions {
          margin-top: 10px;
          display: flex;
          gap: 8px;
          align-items: center;
        }
        .profile-page .node-actions .btn,
        .profile-page .manager-actions .btn {
          border-radius: 999px;
          font-weight: 600;
          padding: 4px 10px;
        }
        .profile-page .node-actions .badge,
        .profile-page .manager-actions .badge {
          padding: 6px 10px;
          font-weight: 700;
        }
        .profile-page .recruiter-list {
          margin-top: 10px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          width: 100%;
        }
        .profile-page .recruiter-item {
          border: 1px dashed #cbd5e1;
          border-radius: 10px;
          padding: 8px 10px;
          font-weight: 600;
          color: #1f2937;
          background: #f8fafc;
          display: flex;
          gap: 8px;
          align-items: center;
          transition: transform 160ms ease, box-shadow 160ms ease;
          border-left: 4px solid #9d174d;
        }
        .profile-page .recruiter-item:hover {
          transform: translateY(-1px);
          box-shadow: 0 8px 16px rgba(15, 42, 80, 0.12);
        }
        .profile-page .tree-muted {
          text-align: center;
          color: #64748b;
          font-size: 13px;
          padding: 16px 0 4px;
        }
        .profile-page .unassigned-section {
          margin-top: 22px;
          padding-top: 14px;
          border-top: 1px dashed #e2e8f0;
        }
        .profile-page .unassigned-title {
          font-weight: 700;
          color: #334155;
          margin-bottom: 10px;
        }
        .profile-page .unassigned-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 14px;
          justify-content: flex-start;
        }
        @keyframes treeFade {
          from {
            opacity: 0;
            transform: translateY(-6px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
      <div className="container-fluid profile-page">
        <div className="row g-4">
          <div className="col-12">
            <div className="profile-hero p-4 mt-3">
              <div className="d-flex align-items-center gap-3 position-relative">
                <div className="avatar-badge">
                  <img
                    src={process.env.PUBLIC_URL + "/admin_assets/images/9.jpg"}
                    alt="Profile"
                    className="rounded-circle"
                    style={{ width: "64px", height: "64px", objectFit: "cover" }}
                  />
                </div>
                <div>
                  <div className="fs-4 fw-semibold">{form.name || "Your Profile"}</div>
                  <div className="text-white-50">
                    {form.email || "Keep your account details up to date."}
                  </div>
                </div>
                <span className="floating-tag">
                  <span className="d-inline-flex align-items-center">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M12 3l2.09 5.26L20 9l-4 3.74.99 5.26L12 15.77 7.01 18 8 12.74 4 9l5.91-.74L12 3z" fill="currentColor" />
                    </svg>
                  </span>
                  Verified
                </span>
              </div>
              <div className="hero-details">
                <div className="hero-detail">
                  <div className="hero-detail-label">Name</div>
                  <div className="hero-detail-value">{form.name || (loading ? "Loading..." : "—")}</div>
                </div>
                <div className="hero-detail">
                  <div className="hero-detail-label">Email</div>
                  <div className="hero-detail-value">{form.email || (loading ? "Loading..." : "—")}</div>
                </div>
                <div className="hero-detail">
                  <div className="hero-detail-label">Mobile</div>
                  <div className="hero-detail-value">{form.mobile || (loading ? "Loading..." : "—")}</div>
                </div>
                <div className="hero-detail">
                  <div className="hero-detail-label">Post</div>
                  <div className="hero-detail-value">{form.post || (loading ? "Loading..." : "—")}</div>
                </div>
              </div>
              <svg
                className="hero-art"
                width="220"
                height="140"
                viewBox="0 0 220 140"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M16 90C58 40 112 14 202 18"
                  stroke="#022045"
                  strokeWidth="18"
                  strokeLinecap="round"
                  opacity="0.3"
                />
                <path
                  d="M22 118C70 78 132 60 196 66"
                  stroke="#022045"
                  strokeWidth="10"
                  strokeLinecap="round"
                  opacity="0.3"
                />
              </svg>
            </div>
          </div>

          <div className="col-lg-12">
            {staffTabs.length > 0 && (
              <div className="profile-card p-4">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="fw-semibold fs-5">Staff Hierarchy</div>
                </div>

                {hierarchy.mode === "tree" ? (
                  <div className="tree-wrap">
                    {hierarchy.cxos?.length > 0 && (
                      <div className="tree-level cxo-row">
                        {hierarchy.cxos.map((cxo) => {
                          const isOpen = !!expandedCxos[cxo.id];
                          return (
                            <div className="tree-node" key={`cxo-${cxo.id}`}>
                              <div
                                className="node-card role-cxo clickable"
                                role="button"
                                tabIndex={0}
                                onClick={() => toggleCxo(cxo.id)}
                              >
                                <div className="node-main">
                                  <img
                                    src={process.env.PUBLIC_URL + "/admin_assets/images/9.jpg"}
                                    alt="Profile"
                                    className="node-avatar"
                                  />
                                  <div>
                                    <div className="node-role">CXO</div>
                                    <div className="node-name">{cxo.name || "CXO"}</div>
                                  </div>
                                </div>
                                <div className="node-actions">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-light"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      toggleCxo(cxo.id);
                                    }}
                                  >
                                    {isOpen ? "Hide heads" : "Show heads"}
                                  </button>
                                  <span className="badge bg-danger rounded-pill">
                                    {cxo.heads?.length || 0}
                                  </span>
                                </div>
                              </div>
                              {isOpen && cxo.heads?.length > 0 && (
                                <div className="tree-children">
                                  {cxo.heads.map((head) => {
                                    const headOpen = !!expandedHeads[head.id];
                                    return (
                                      <div className="tree-node" key={`head-${cxo.id}-${head.id}`}>
                                        <div
                                          className="node-card role-head clickable"
                                          role="button"
                                          tabIndex={0}
                                          onClick={() => toggleHead(head.id)}
                                        >
                                          <div className="node-main">
                                            <img
                                              src={process.env.PUBLIC_URL + "/admin_assets/images/9.jpg"}
                                              alt="Profile"
                                              className="node-avatar"
                                            />
                                            <div>
                                              <div className="node-role">Sourcing Head</div>
                                              <div className="node-name">
                                                {head.name || "Sourcing Head"}
                                              </div>
                                            </div>
                                          </div>
                                          <div className="node-actions">
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-light"
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                toggleHead(head.id);
                                              }}
                                            >
                                              {headOpen ? "Hide managers" : "Show managers"}
                                            </button>
                                            <span className="badge bg-danger rounded-pill">
                                              {head.managers?.length || 0}
                                            </span>
                                          </div>
                                        </div>
                                        {headOpen && head.managers?.length > 0 && (
                                          <div className="tree-children">
                                            {head.managers.map((manager) => {
                                              const recruiterCount = manager.recruiters?.length || 0;
                                              const isOpen = !!expandedManagers[manager.id];
                                              return (
                                                <div
                                                  className="tree-node"
                                                  key={`mgr-${head.id}-${manager.id}`}
                                                >
                                                  <div
                                                    className="node-card role-manager clickable"
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => toggleManager(manager.id)}
                                                  >
                                                    <div className="node-main">
                                                      <img
                                                        src={process.env.PUBLIC_URL + "/admin_assets/images/9.jpg"}
                                                        alt="Profile"
                                                        className="node-avatar"
                                                      />
                                                      <div>
                                                        <div className="node-role">Sourcing Manager</div>
                                                        <div className="node-name">
                                                          {manager.name || "Sourcing Manager"}
                                                        </div>
                                                      </div>
                                                    </div>
                                                    <div className="manager-actions">
                                                      <button
                                                        type="button"
                                                        className="btn btn-sm btn-light"
                                                        onClick={(event) => {
                                                          event.stopPropagation();
                                                          toggleManager(manager.id);
                                                        }}
                                                      >
                                                        {isOpen ? "Hide recruiters" : "Show recruiters"}
                                                      </button>
                                                      <span className="badge bg-danger rounded-pill">
                                                        {recruiterCount}
                                                      </span>
                                                    </div>
                                                    {isOpen && recruiterCount > 0 && (
                                                      <div className="recruiter-list">
                                                        {manager.recruiters.map((person, idx) => (
                                                          <div
                                                            className="recruiter-item role-recruiter"
                                                            key={`rec-${manager.id}-${person.id || idx}`}
                                                          >
                                                            <img
                                                              src={
                                                                process.env.PUBLIC_URL +
                                                                "/admin_assets/images/9.jpg"
                                                              }
                                                              alt="Profile"
                                                              className="node-avatar"
                                                            />
                                                            <span>{person?.name || "â€”"}</span>
                                                          </div>
                                                        ))}
                                                      </div>
                                                    )}
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {hierarchy.cxos?.length === 0 && hierarchy.heads?.length > 0 && (
                      <div className="tree-level">
                        {hierarchy.heads.map((head) => {
                          const headOpen = !!expandedHeads[head.id];
                          return (
                            <div className="tree-node" key={`head-root-${head.id}`}>
                              <div
                                className="node-card role-head clickable"
                                role="button"
                                tabIndex={0}
                                onClick={() => toggleHead(head.id)}
                              >
                                <div className="node-main">
                                  <img
                                    src={process.env.PUBLIC_URL + "/admin_assets/images/9.jpg"}
                                    alt="Profile"
                                    className="node-avatar"
                                  />
                                  <div>
                                    <div className="node-role">Sourcing Head</div>
                                    <div className="node-name">{head.name || "Sourcing Head"}</div>
                                  </div>
                                </div>
                                <div className="node-actions">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-light"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      toggleHead(head.id);
                                    }}
                                  >
                                    {headOpen ? "Hide managers" : "Show managers"}
                                  </button>
                                  <span className="badge bg-danger rounded-pill">
                                    {head.managers?.length || 0}
                                  </span>
                                </div>
                              </div>
                              {headOpen && head.managers?.length > 0 && (
                                <div className="tree-children">
                                  {head.managers.map((manager) => {
                                    const recruiterCount = manager.recruiters?.length || 0;
                                    const isOpen = !!expandedManagers[manager.id];
                                    return (
                                      <div className="tree-node" key={`mgr-root-${head.id}-${manager.id}`}>
                                        <div
                                          className="node-card role-manager clickable"
                                          role="button"
                                          tabIndex={0}
                                          onClick={() => toggleManager(manager.id)}
                                        >
                                          <div className="node-main">
                                            <img
                                              src={process.env.PUBLIC_URL + "/admin_assets/images/9.jpg"}
                                              alt="Profile"
                                              className="node-avatar"
                                            />
                                            <div>
                                              <div className="node-role">Sourcing Manager</div>
                                              <div className="node-name">
                                                {manager.name || "Sourcing Manager"}
                                              </div>
                                            </div>
                                          </div>
                                          <div className="manager-actions">
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-light"
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                toggleManager(manager.id);
                                              }}
                                            >
                                              {isOpen ? "Hide recruiters" : "Show recruiters"}
                                            </button>
                                            <span className="badge bg-danger rounded-pill">
                                              {recruiterCount}
                                            </span>
                                          </div>
                                          {isOpen && recruiterCount > 0 && (
                                            <div className="recruiter-list">
                                              {manager.recruiters.map((person, idx) => (
                                                <div
                                                  className="recruiter-item role-recruiter"
                                                  key={`rec-root-${manager.id}-${person.id || idx}`}
                                                >
                                                  <img
                                                    src={
                                                      process.env.PUBLIC_URL +
                                                      "/admin_assets/images/9.jpg"
                                                    }
                                                    alt="Profile"
                                                    className="node-avatar"
                                                  />
                                                  <span>{person?.name || "â€”"}</span>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {hierarchy.cxos?.length === 0 &&
                      hierarchy.heads?.length === 0 &&
                      hierarchy.managers?.length > 0 && (
                        <div className="tree-level">
                          {hierarchy.managers.map((manager) => {
                            const recruiterCount = manager.recruiters?.length || 0;
                            const isOpen = !!expandedManagers[manager.id];
                            return (
                              <div className="tree-node" key={`mgr-only-${manager.id}`}>
                                <div className="node-card">
                                  <div className="node-role">Sourcing Manager</div>
                                  <div className="node-name">{manager.name || "Sourcing Manager"}</div>
                                  <div className="manager-actions">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-light"
                                      onClick={() => toggleManager(manager.id)}
                                    >
                                      {isOpen ? "Hide recruiters" : "Show recruiters"}
                                    </button>
                                    <span className="badge bg-danger rounded-pill">
                                      {recruiterCount}
                                    </span>
                                  </div>
                                  {isOpen && recruiterCount > 0 && (
                                    <div className="recruiter-list">
                                      {manager.recruiters.map((person, idx) => (
                                        <div
                                          className="recruiter-item"
                                          key={`rec-only-${manager.id}-${person.id || idx}`}
                                        >
                                          {person?.name || "â€”"}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                    {isAdminView &&
                      (hierarchy.unassignedHeads?.length > 0 ||
                        hierarchy.unassignedManagers?.length > 0 ||
                        hierarchy.unassignedRecruiters?.length > 0 ||
                        hierarchy.cxos?.some((cxo) => (cxo.heads?.length || 0) === 0)) && (
                      <div className="unassigned-section">
                        <div className="unassigned-title">Unassigned</div>

                        {hierarchy.cxos?.some((cxo) => (cxo.heads?.length || 0) === 0) && (
                          <>
                            <div className="node-role mb-2">CXO (No Heads)</div>
                            <div className="unassigned-grid">
                              {hierarchy.cxos
                                .filter((cxo) => (cxo.heads?.length || 0) === 0)
                                .map((cxo) => (
                                  <div className="node-card role-cxo" key={`un-cxo-${cxo.id}`}>
                                    <div className="node-main">
                                      <img
                                        src={process.env.PUBLIC_URL + "/admin_assets/images/9.jpg"}
                                        alt="Profile"
                                        className="node-avatar"
                                      />
                                      <div>
                                        <div className="node-role">CXO</div>
                                        <div className="node-name">{cxo.name || "CXO"}</div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </>
                        )}

                        {hierarchy.unassignedHeads?.length > 0 && (
                          <>
                            <div className="node-role mb-2">Sourcing Heads (No CXO)</div>
                            <div className="unassigned-grid">
                              {hierarchy.unassignedHeads.map((head) => (
                                <div className="tree-node" key={`un-head-${head.id}`}>
                                  <div
                                    className="node-card role-head clickable"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => toggleHead(head.id)}
                                  >
                                    <div className="node-main">
                                      <img
                                        src={process.env.PUBLIC_URL + "/admin_assets/images/9.jpg"}
                                        alt="Profile"
                                        className="node-avatar"
                                      />
                                      <div>
                                        <div className="node-role">Sourcing Head</div>
                                        <div className="node-name">{head.name || "Sourcing Head"}</div>
                                      </div>
                                    </div>
                                    <div className="node-actions">
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-light"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          toggleHead(head.id);
                                        }}
                                      >
                                        {expandedHeads[head.id] ? "Hide managers" : "Show managers"}
                                      </button>
                                      <span className="badge bg-danger rounded-pill">
                                        {head.managers?.length || 0}
                                      </span>
                                    </div>
                                  </div>
                                  {expandedHeads[head.id] && head.managers?.length > 0 && (
                                    <div className="tree-children">
                                      {head.managers.map((manager) => {
                                        const recruiterCount = manager.recruiters?.length || 0;
                                        const isOpen = !!expandedManagers[manager.id];
                                        return (
                                          <div className="tree-node" key={`un-mgr-${head.id}-${manager.id}`}>
                                            <div
                                              className="node-card role-manager clickable"
                                              role="button"
                                              tabIndex={0}
                                              onClick={() => toggleManager(manager.id)}
                                            >
                                              <div className="node-main">
                                                <img
                                                  src={process.env.PUBLIC_URL + "/admin_assets/images/9.jpg"}
                                                  alt="Profile"
                                                  className="node-avatar"
                                                />
                                                <div>
                                                  <div className="node-role">Sourcing Manager</div>
                                                  <div className="node-name">
                                                    {manager.name || "Sourcing Manager"}
                                                  </div>
                                                </div>
                                              </div>
                                              <div className="manager-actions">
                                                <button
                                                  type="button"
                                                  className="btn btn-sm btn-light"
                                                  onClick={(event) => {
                                                    event.stopPropagation();
                                                    toggleManager(manager.id);
                                                  }}
                                                >
                                                  {isOpen ? "Hide recruiters" : "Show recruiters"}
                                                </button>
                                                <span className="badge bg-danger rounded-pill">
                                                  {recruiterCount}
                                                </span>
                                              </div>
                                              {isOpen && recruiterCount > 0 && (
                                                <div className="recruiter-list">
                                                  {manager.recruiters.map((person, idx) => (
                                                    <div
                                                      className="recruiter-item role-recruiter"
                                                      key={`un-rec-${manager.id}-${person.id || idx}`}
                                                    >
                                                      <img
                                                        src={
                                                          process.env.PUBLIC_URL +
                                                          "/admin_assets/images/9.jpg"
                                                        }
                                                        alt="Profile"
                                                        className="node-avatar"
                                                      />
                                                      <span>{person?.name || "—"}</span>
                                                    </div>
                                                  ))}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </>
                        )}

                        {hierarchy.unassignedManagers?.length > 0 && (
                          <>
                            <div className="node-role mb-2 mt-3">Managers (No Head)</div>
                            <div className="unassigned-grid">
                              {hierarchy.unassignedManagers.map((manager) => (
                                <div className="tree-node" key={`un-mgr-${manager.id}`}>
                                  <div
                                    className="node-card role-manager clickable"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => toggleManager(manager.id)}
                                  >
                                    <div className="node-main">
                                      <img
                                        src={process.env.PUBLIC_URL + "/admin_assets/images/9.jpg"}
                                        alt="Profile"
                                        className="node-avatar"
                                      />
                                      <div>
                                        <div className="node-role">Sourcing Manager</div>
                                        <div className="node-name">
                                          {manager.name || "Sourcing Manager"}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="manager-actions">
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-light"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          toggleManager(manager.id);
                                        }}
                                      >
                                        {expandedManagers[manager.id]
                                          ? "Hide recruiters"
                                          : "Show recruiters"}
                                      </button>
                                      <span className="badge bg-danger rounded-pill">
                                        {manager.recruiters?.length || 0}
                                      </span>
                                    </div>
                                    {expandedManagers[manager.id] &&
                                      (manager.recruiters?.length || 0) > 0 && (
                                        <div className="recruiter-list">
                                          {manager.recruiters.map((person, idx) => (
                                            <div
                                              className="recruiter-item role-recruiter"
                                              key={`un-rec2-${manager.id}-${person.id || idx}`}
                                            >
                                              <img
                                                src={
                                                  process.env.PUBLIC_URL +
                                                  "/admin_assets/images/9.jpg"
                                                }
                                                alt="Profile"
                                                className="node-avatar"
                                              />
                                              <span>{person?.name || "—"}</span>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </>
                        )}

                        {hierarchy.unassignedRecruiters?.length > 0 && (
                          <>
                            <div className="node-role mb-2 mt-3">Recruiters (No Manager)</div>
                            <div className="unassigned-grid">
                              {hierarchy.unassignedRecruiters.map((person, idx) => (
                                <div
                                  className="recruiter-item role-recruiter"
                                  key={`un-rec-${person.id || idx}`}
                                >
                                  <img
                                    src={process.env.PUBLIC_URL + "/admin_assets/images/9.jpg"}
                                    alt="Profile"
                                    className="node-avatar"
                                  />
                                  <span>{person?.name || "—"}</span>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <nav className="nav nav-style-1 nav-pills gap-2 mb-3" role="tablist">
                      {staffTabs.map((key) => (
                        <button
                          key={key}
                          className={`nav-link ${activeStaffTab === key ? "active" : ""}`}
                          onClick={() => setActiveStaffTab(key)}
                          type="button"
                        >
                          {key.replace(/_/g, " ")}
                          <span className="badge bg-danger ms-1 rounded-pill">
                            {uniqueStaffByTab[key]?.length || 0}
                          </span>
                        </button>
                      ))}
                    </nav>

                    <div className="row g-2">
                      {(uniqueStaffByTab[activeStaffTab] || []).map((person, index) => (
                        <div className="col-md-6" key={`${activeStaffTab}-${person?.id || index}-${index}`}>
                          <div className="d-flex align-items-center border rounded-3 px-3 py-2">
                            <div className="fw-semibold text-truncate">{person?.name || "â€”"}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="tree-muted">Hierarchy data not available for this user yet.</div>
                  </>
                )}              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile;


